import { type DeauthenticateUserMutation$data } from '#app/__generated__/relay/DeauthenticateUserMutation.graphql.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useAuth } from '#app/hooks/useAuth.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { usePostHog } from '#app/hooks/usePostHog.js';
import { useURLParameters } from '#app/hooks/useURLParameters.js';
import { getRoute } from '@contra/routes';
import { useCallback, useEffect, useRef } from 'react';

export const LogOutWrapper = () => {
  const { logout } = useAuth();
  const navigation = useNavigation();
  const { featureFlagsLoaded } = usePostHog();
  const toast = useToast();

  // We only want to show the success toast once
  // but it'll likely try to render multiple times
  // since we need to fire logout in a useEffect
  // with changing dependencies
  const hasShownSuccessToast = useRef(false);

  const [urlSearchParameters] = useURLParameters('/log-out');
  const redirectTo = urlSearchParameters.redirectTo;

  const onLogoutComplete = useCallback(
    ({ deauthenticate }: DeauthenticateUserMutation$data) => {
      if (deauthenticate.errors?.[0]) {
        const presentableError = deauthenticate.errors[0];
        toast.error(presentableError.message);
      } else {
        if (!hasShownSuccessToast.current) {
          toast.success('You have been successfully logged out');
          hasShownSuccessToast.current = true;
        }

        if (redirectTo) {
          navigation.push(redirectTo);
        } else {
          navigation.push(getRoute('/log-in'));
        }
      }
    },
    [navigation, redirectTo, toast],
  );

  useEffect(() => {
    // When visiting /log-out directly,
    // we need to make sure posthog has loaded
    // before we reset the current users identity
    if (featureFlagsLoaded) {
      logout({
        onCompleted: onLogoutComplete,
        variables: {},
      });
    }
  }, [featureFlagsLoaded, logout, onLogoutComplete]);

  return null;
};
